import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { FormBuilderSelectModule, FormsBuilderModule } from '@monsido/angular-shared-components';
import { DomainGroupMembersEntity, DomainUsersEntity } from '@monsido/modules/models/api/interfaces/user.interface';
import { TranslateModule } from 'app/modules/translate.module';
import { UserImportCsvService } from '../../services/user-import-csv.service';

export type UserImportSelectedModel = {
    emailIndex?: number,
    passwordIndex?: number,
    isAdminIndex?: number,
    firstNameIndex?: number,
    lastNameIndex?: number,
    domainIdsColumnIndex?: number,
    domainGroupIdsColumnIndex?: number,
    domainGroups?: DomainGroupMembersEntity[],
    domains?: DomainUsersEntity[],
};

export type UserImportCSVData = {
    data: string[][],
    format?: CSVFormat, // Optional format property, defaults to 'basic' if not set
};

// Enhanced types for format detection (Step 1)
export type CSVFormat = 'basic' | 'enhanced';

export type CSVFormatMetadata = {
    hasDomainIdsColumn: boolean,
    hasDomainGroupIdsColumn: boolean,
    domainIdsColumnIndex?: number,
    domainGroupIdsColumnIndex?: number,
};

export type UserImportCSVDataEnhanced = {
    data: string[][],
    format: CSVFormat,
    metadata: CSVFormatMetadata,
};

export type UserImportSelectedModelEnhanced = {
    emailIndex?: number,
    passwordIndex?: number,
    isAdminIndex?: number,
    firstNameIndex?: number,
    lastNameIndex?: number,
    domainIdsColumnIndex?: number,
    domainGroupIdsColumnIndex?: number,
    domainGroups?: DomainGroupMembersEntity[],
    domains?: DomainUsersEntity[],
};

type UserImportCSVDataOption = {
    name: string,
    value: number,
};

@Component({
    selector: 'mon-form-user-import-setup',
    templateUrl: './setup.component.html',
    imports: [
        FormsModule,
        FormsBuilderModule,
        FormBuilderSelectModule,
        TranslateModule,
    ],
    styles: [`
        .format-indicator {
            background-color: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            padding: 12px;
            margin-bottom: 20px;
            font-size: 14px;
        }
        .format-type {
            color: #28a745;
            font-weight: 500;
        }
    `],
})
export class FormUserImportSetupComponent implements OnInit {
    @Input() data: UserImportCSVData;
    @Input() selected: UserImportSelectedModel = {};
    @ViewChild('userImportSetupForm') userImportSetupForm: NgForm;

    options: UserImportCSVDataOption[] = [];

    constructor (private userImportCsvService: UserImportCsvService) {}

    /**
     * Detects CSV format based on headers - enhanced or basic
     */
    detectCSVFormat (data: UserImportCSVData): CSVFormat {
        return this.userImportCsvService.detectCSVFormat(data);
    }

    /**
     * Parse domain IDs from a CSV cell value
     * Handles comma-separated values and quoted strings
     * @param cellValue The raw cell value from CSV
     * @returns Array of domain IDs as numbers
     */
    parseDomainIds (cellValue: string): number[] {
        return this.userImportCsvService.parseIds(cellValue);
    }

    /**
     * Parse domain group IDs from a CSV cell value
     * Handles comma-separated values and quoted strings
     * @param cellValue The raw cell value from CSV
     * @returns Array of domain group IDs as numbers
     */
    parseDomainGroupIds (cellValue: string): number[] {
        return this.userImportCsvService.parseIds(cellValue);
    }

    /**
     * Find the column index for domain IDs in the CSV headers
     * @returns Column index or -1 if not found
     */
    findDomainIdsColumnIndex (): number {
        if (!this.data?.data || this.data.data.length === 0) {
            return -1;
        }

        const headers = this.data.data[0];
        return this.userImportCsvService.findDomainIdsColumnIndex(headers);
    }

    /**
     * Find the column index for domain group IDs in the CSV headers
     * @returns Column index or -1 if not found
     */
    findDomainGroupIdsColumnIndex (): number {
        if (!this.data?.data || this.data.data.length === 0) {
            return -1;
        }

        const headers = this.data.data[0];
        return this.userImportCsvService.findDomainGroupIdsColumnIndex(headers);
    }

    ngOnInit (): void {
        if (!this.data?.data || !Array.isArray(this.data.data) || this.data.data.length === 0) {
            return;
        }

        const datum = this.data.data[0];

        if (!Array.isArray(datum)) {
            return;
        }

        // Detect and store CSV format
        const detectedFormat = this.detectCSVFormat(this.data);
        this.data.format = detectedFormat;

        // Store domain column indices for enhanced format
        if (detectedFormat === 'enhanced') {
            this.selected.domainIdsColumnIndex = this.findDomainIdsColumnIndex();
            this.selected.domainGroupIdsColumnIndex = this.findDomainGroupIdsColumnIndex();
        }

        // Clear options array to prevent duplicates on re-initialization
        this.options = [];

        for (let i = 0; i < datum.length; i++) {
            this.options.push({ name: datum[i], value: i });
        }

        for (const option of this.options) {
            const index = option.value;
            const normalizedName = option.name.toLowerCase().trim().replace(/[_\s]+/g, ' ');

            // Email field patterns
            if (this.matchesPattern(normalizedName, ['email', 'e mail', 'email address', 'emailaddress', 'mail'])) {
                this.selected.emailIndex = index;
            } else if (this.matchesPattern(normalizedName, ['password', 'pass', 'pwd'])) {
                this.selected.passwordIndex = index;
            } else if (this.matchesPattern(normalizedName, ['is admin', 'isadmin', 'admin', 'is administrator', 'administrator'])) {
                this.selected.isAdminIndex = index;
            } else if (this.matchesPattern(normalizedName, ['first name', 'firstname', 'first', 'given name', 'givenname'])) {
                this.selected.firstNameIndex = index;
            } else if (this.matchesPattern(normalizedName, ['last name', 'lastname', 'last', 'surname', 'family name', 'familyname'])) {
                this.selected.lastNameIndex = index;
            }
        }
    }

    /**
     * Helper method to check if a column name matches any of the expected patterns
     */
    private matchesPattern (columnName: string, patterns: string[]): boolean {
        return patterns.some(pattern => columnName === pattern || columnName.includes(pattern));
    }

    isFormValid (): boolean {
        return this.userImportSetupForm ? this.userImportSetupForm.valid : false;
    }
}
